import * as React from "react";
import "./styles.scss";
import { dummyPlayerCard, PlayerCardTheme } from "../../../../services/bedrock";
import { useContext, useEffect, useMemo } from "react";
import ThemePreview from "./ThemePreview";
import _ from "lodash";
import ThemeModal from "./ThemeModal";
import { Icon, Loader } from "semantic-ui-react";
import { PlayerCardSettingsContext } from "../../Contexts";

interface ThemesTabProps {
  active: boolean;
}

const ThemesTab: React.FC<ThemesTabProps> = ({ active }) => {
  const { Themes, Preview, PlayerCards, User } = useContext(PlayerCardSettingsContext);
  if (User.playerId === undefined && User.coachId === undefined) return null;

  const ownerType: "player" | "coach" = User.isPlayer ? "player" : "coach";
  const ownerId = User.isPlayer ? User.playerId! : User.coachId!;
  const ownerIdKey = `${ownerType}Id` as keyof PlayerCardTheme;

  const newTheme: PlayerCardTheme = {
    id: -Date.now(),
    coachId: null,
    playerId: null,
    player: null,
    name: "",
    pageBackground: "#FFFFFF",
    pageForeground: "#000000",
    widgetBackground: "#000000",
    widgetForeground: "#FFFFFF",
  };
  (newTheme as any)[ownerIdKey] = ownerId;

  useEffect(() => {
    if (!active || Themes.inEditMode) return;
    if (Themes.selectedTheme) {
      const playerCard =
        ownerType === "player" && PlayerCards.selectedPlayerCard ? PlayerCards.selectedPlayerCard : dummyPlayerCard;
      Preview.setPlayerCard({ ...playerCard, themeId: Themes.selectedTheme.id, theme: Themes.selectedTheme });
    } else {
      Preview.setPlayerCard(undefined);
    }
  }, [active, Themes.inEditMode, Themes.selectedTheme]);

  useEffect(() => {
    if (Themes.inEditMode) return;
    if (!Themes.selectedTheme || !Themes.themes.find(theme => theme.id === Themes.selectedTheme?.id)) {
      const theme = PlayerCards.selectedPlayerCard?.theme || Themes.themes[0];
      Themes.setSelectedTheme(theme);
    }
  }, [Themes.themes, Themes.inEditMode, Themes.selectedTheme]);

  const themeGroups = useMemo(() => {
    const groups: { [key: string]: PlayerCardTheme[] } = {
      "Your Themes": [],
      "Player Themes": [],
      "Coach Themes": [],
      "System Themes": [],
    };
    const effectiveThemes = [...Themes.themes];
    const selectedTheme = PlayerCards.selectedPlayerCard?.theme;
    if (selectedTheme && selectedTheme.id > 0 && !effectiveThemes.find(t => t.id === selectedTheme.id)) {
      effectiveThemes.push(selectedTheme);
    }

    effectiveThemes.forEach(theme => {
      if (theme[ownerIdKey] === ownerId) {
        groups["Your Themes"].push(theme);
      } else if (theme.playerId !== null) {
        groups["Player Themes"].push(theme);
      } else if (theme.coachId !== null && theme.coachId !== -1) {
        groups["Coach Themes"].push(theme);
      } else {
        groups["System Themes"].push(theme);
      }
    });
    if (User.hasWritePermission) {
      groups["Your Themes"].push(newTheme);
    }

    return groups;
  }, [Themes.themes, PlayerCards.selectedPlayerCard?.theme]);

  const handleThemeUpdated = (theme: PlayerCardTheme) => {
    Themes.setInEditMode(false);
  };

  const createNewTheme = () => {
    Themes.setSelectedTheme(newTheme);
    Themes.setInEditMode(true);
  };

  if (Themes.inEditMode && Themes.selectedTheme) {
    return (
      <ThemeModal
        active={active}
        theme={Themes.selectedTheme}
        onThemeSaved={handleThemeUpdated}
        onClose={() => Themes.setInEditMode(false)}
      />
    );
  }

  return (
    <div className={`playerCardThemesTab ${active ? "active" : ""}`}>
      <div className="themeGroups">
        {Object.entries(themeGroups).map(([owner, themes]) => {
          if (themes.length === 0) return null;
          return (
            <div key={owner} className="themeGroup">
              <h2 className="themesHeader">
                {owner}
                {Themes.isLoading && <Loader active inline size="small" />}
              </h2>
              <div className="themeCardsGrid">
                {themes.map(theme => {
                  if (theme.id < 0) {
                    return (
                      <div
                        key={`theme-${theme.id}`}
                        className="playerCardThemePreview clickable newTheme"
                        onClick={createNewTheme}
                      >
                        <Icon name="plus" />
                        Create New Theme
                      </div>
                    );
                  }
                  return (
                    <div key={`theme-${theme.id}`} className="themeCardContainer">
                      <ThemePreview theme={theme} currentUserIsOwner={theme[ownerIdKey] === ownerId} />
                      <div className="themeCardInfo">
                        <div className="themeName" title={theme.name}>
                          {theme.name}
                        </div>
                        {theme[ownerIdKey] !== ownerId && theme.player && (
                          <div className="themeOwner">{theme.player.fullName}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
export default ThemesTab;
